import * as Yup from 'yup';
import { AuthFetch, fetchFromRestAPI } from './api';
import moment from 'moment';
import 'moment/locale/de';
import i18next from 'i18next';
import { isValidDate } from './dateTimeUtils';
import { decryptText } from './crypto';
import { VITE_AUTH0_SECRET_INDEX_KEY } from '../config';
import { IdToken } from '@auth0/auth0-react';
import { IErrorDto, ITherapist, ITherapistPlan } from "./types";
import { IErrorDtoTypeGuard } from "./typeUtils";

export type UserProfileData = ITherapist & {
  date?: number,
  month?: number,
  year?: number,
}

export const userProfileInitData: UserProfileData = {
  _id: undefined,
  firstName: '',
  lastName: '',
  street: '',
  postal: '',
  city: '',
  approved: false,
  birthDate: undefined,
  telephone: '',
  date: undefined,
  month: undefined,
  year: undefined,
  country: '',
  isCallAllowed: true,
  therapistPlan: undefined,
  profilePic: undefined,
}
export const UserAccountSchema = Yup.object().shape({
  firstName: Yup.string().required(i18next.t('accountSetup.promptRequiredFirstName')),
  lastName: Yup.string().required(i18next.t('accountSetup.promptRequiredFirstName')),
  street: Yup.string().required(i18next.t('accountSetup.promptRequiredFirstName')),
  postal: Yup.string().required(i18next.t('accountSetup.promptRequiredFirstName')),
  city: Yup.string().required(i18next.t('accountSetup.promptRequiredFirstName')),
  telephone: Yup.string().required(i18next.t('accountSetup.promptRequiredFirstName')),
  therapistPlan: Yup.string().required(i18next.t('accountSetup.promptRequiredFirstName')).notOneOf(['undefined']),
  birthDate: Yup.date()
      .required(i18next.t('accountSetup.promptRequiredFirstName'))
      .max(new Date(Date.now() - 1000 * 60 * 60 * 24 * 365 * 18), i18next.t('accountSetup.promptValidDate')) // at least 18 years old
      .min(new Date('1920-01-01'), i18next.t('accountSetup.promptValidDate')), // max. 100 years old
});

export const validateUserForm = (values:  UserProfileData) => {
  const errors: Partial<Record<keyof UserProfileData, string>> = {};
  if (!values.firstName) {
    errors['firstName'] = i18next.t('accountSetup.firstNameRequired');
  } else if (!values.lastName) {
    errors['lastName'] = i18next.t('accountSetup.lastNameRequired');
  } else if (!values.street) {
    errors['street'] = i18next.t('accountSetup.pleaseFillIn');
  } else if (!values.postal) {
    errors['postal'] = i18next.t('accountSetup.pleaseFillIn');
  } else if (!values.city) {
    errors['city'] = i18next.t('accountSetup.pleaseFillIn');
  } else if (!values.telephone) {
    errors['telephone'] = i18next.t('accountSetup.pleaseFillIn');
  } else if (values.telephone && !(/^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-.0-9]{8,14}$/g).test(values.telephone)) {
    errors['telephone'] = i18next.t('accountSetup.invalidPhone');
  } else if (values.date && values.month && values.year) {
    if (isValidDate(values.month + "/" + values.date + "/" + values.year)) {
      const differenceYears = moment().diff(`${values.year}-${values.month}-${values.date}`, 'years');
      if (differenceYears < 18 || differenceYears > 100) {
        errors['date'] = i18next.t('accountSetup.ageValidation');
      }
    } else {
      errors['date'] = i18next.t('accountSetup.ageValidation');
    }
  } else {
    errors['date'] = i18next.t('accountSetup.pleaseFillIn');
  }
  return errors;
};

export async function tryFetchUserAccountRemote(authFetch: AuthFetch): Promise<ITherapist | IErrorDto | null> {
  return await authFetch('/api/v1/user-profile', {
    method: 'GET',
  });
}

export async function fetchUserAccountRemote(authFetch: AuthFetch): Promise<ITherapist | null> {
  const userProfile = await authFetch('/api/v1/user-profile', {
    method: 'GET',
  });
  if (userProfile && userProfile.error) {
    return null;
  }
  return userProfile;
}

export async function fetchTherapistKey(authFetch: AuthFetch, idToken: IdToken): Promise<string | null> {
  const auth0Key = idToken[VITE_AUTH0_SECRET_INDEX_KEY];
  const keyResponse = await authFetch('/api/v1/chat/key', {
    method: 'GET',
  });
  if (keyResponse && keyResponse.key) {
    return decryptText(auth0Key, keyResponse.key);
  }

  return null;
}

export async function fetchTherapistPlan(): Promise<ITherapistPlan[] | null> {
  const plans = await fetchFromRestAPI('/api/v1/therapist/plans', {
    method: 'GET'
  });
  if (plans && plans.error) {
    return null;
  }
  return plans;
}

export async function isAccountComplete(authFetch: AuthFetch) {
  const userAccountRemote = await tryFetchUserAccountRemote(authFetch);
  if (!userAccountRemote) {
    return false;
  } else if (IErrorDtoTypeGuard(userAccountRemote)) {
    return false;
  }
  return UserAccountSchema.isValidSync(userAccountRemote);
}

export async function updateUserAccountRemote(authFetch: AuthFetch, userProfileData: ITherapist) {
  const userProfileRecord = await authFetch('/api/v1/user-profile', {
    method: 'POST',
    body: userProfileData,
  });
  return userProfileRecord;
}

export async function upgradeTherapistPlan(authFetch: AuthFetch, body) {
  const response = await authFetch('/api/v1/therapist/upgrade-plan', {
    method: 'POST',
    body: body,
  });
  return response;
}

export async function fetchTherapistCurrentPlan(authFetch: AuthFetch) {
  const plans = await authFetch('/api/v1/therapist/current-plan', {
    method: 'GET',
  });
  if (plans && plans.error) {
    return null;
  }
  return plans;
}

export async function cancelTherapistPlan(authFetch: AuthFetch) {
  const response = await authFetch('/api/v1/therapist/cancel-plan', {
    method: 'POST',
  });
  return response;
}
export async function fetchUserAccountForSetup(authFetch: AuthFetch) {
  const userProfile = await authFetch('/api/v1/user-profile/verify-account', {
    method: 'GET',
  });
  if (userProfile && userProfile.error) {
    return null;
  }
  return userProfile;
}
